import {
	filtersPolishSelector,
	isInFiltersPolishExperiment,
} from '@client/redux/experiments/selectors/filters-polish';
import { useAppDispatch, useAppSelector } from '@client/redux/hooks';
import { getStagedFilteredResultsCtaText } from '@components/shared/PillWithDropdown/Dropdown/containers/Mobile/utils';
import { useScrollLock } from '@hooks/useScrollLock';
import useTrackFilterVendors from '@hooks/useTrackFilterVendors';
import {
	isPricingInSidebarAtom,
	isSidebarOpenAtom,
	noSidebarChangesAtom,
	selectedSidebarFiltersAtom,
} from '@pages/VendorsSearch/containers/FilterPills/components/Sidebar/atoms';
import { useSaveSidebarSelections } from '@pages/VendorsSearch/containers/FilterPills/hooks/use-save-sidebar-selections';
import { useStagedResultsCount } from '@pages/VendorsSearch/containers/FilterPills/hooks/use-staged-results-count';
import { reportServerSideExperiment } from '@redux/experiments';
import { experiments } from '@settings';
import { CtaButton } from '@tkww/orion-web-ds-action';
import { Logger } from '@utils/logger/logger';
import {
	Pane,
	PrimaryCTA,
	SecondaryCTA,
	Sidebar as SidebarUnion,
} from '@xo-union/tk-ui-sidebar';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { type FC, useEffect, useMemo, useRef } from 'react';
import { afterNextPaint } from '../../../../../../../helpers/after-next-paint';
import { appliedFiltersAtom, stagedResultsCountAtom } from '../../atoms';
import SidebarOptions from '../SidebarOptions';
import { useStartingPriceFilter } from '../StartingPriceFilter/use-starting-price-filter';
import MobileHeader from './MobileHeader';
import Styles from './styles.scss';

const Sidebar: FC = () => {
	const isMobile = useAppSelector((state) => state.viewport.isMobile);
	const displayMarketCode = useAppSelector(
		(state) => state.settings.marketCode,
	);
	useStagedResultsCount();

	const stagedResultsCount = useAtomValue(stagedResultsCountAtom);
	const { clear, resetStagedMinMax } = useStartingPriceFilter();
	const [isSidebarOpen, setIsSidebarOpen] = useAtom(isSidebarOpenAtom);
	const appliedFilters = useAtomValue(appliedFiltersAtom);
	const selectedSidebarFilters = useAtomValue(selectedSidebarFiltersAtom);
	const setSelectedSidebarFilters = useSetAtom(selectedSidebarFiltersAtom);
	const noSidebarChanges = useAtomValue(noSidebarChangesAtom);
	const isPricingInSidebar = useAtomValue(isPricingInSidebarAtom);
	const saveSidebarSelections = useSaveSidebarSelections();
	const sidebarContentRef = useRef<HTMLDivElement>(null);
	useScrollLock(isSidebarOpen, sidebarContentRef);
	const [trackVendors] = useTrackFilterVendors({ displayMarketCode });

	const filtersPolishAssignment = useAppSelector(filtersPolishSelector);
	const isInFiltersPolishExperimentVariant = useAppSelector(
		isInFiltersPolishExperiment,
	);
	const dispatch = useAppDispatch();

	useEffect(() => {
		if (isSidebarOpen && filtersPolishAssignment) {
			Logger.log({
				log_message: `Filters Polish Assignment: ${filtersPolishAssignment}`,
			});
			void dispatch(reportServerSideExperiment(experiments.filtersPolish));
		}
	}, [filtersPolishAssignment, dispatch, isSidebarOpen]);

	const onClose = (): void => {
		afterNextPaint(() => {
			setIsSidebarOpen(false);
			resetStagedMinMax();
		});
	};

	const primary = () => {
		afterNextPaint(() => {
			setIsSidebarOpen(false);
			saveSidebarSelections();
			trackVendors('save selected filters');
		});
	};

	const ctaText = useMemo(() => {
		return getStagedFilteredResultsCtaText(stagedResultsCount);
	}, [stagedResultsCount]);

	const renderPrimaryCTA = () => {
		if (isMobile) {
			return (
				<CtaButton
					block="sm"
					styleType="primary"
					size="md"
					onClick={primary}
					disabled={stagedResultsCount === 0 || noSidebarChanges}
				>
					{ctaText}
				</CtaButton>
			);
		}

		return (
			<PrimaryCTA
				disabled={
					noSidebarChanges ||
					(isInFiltersPolishExperimentVariant && !stagedResultsCount)
				}
				onClick={primary}
				style={
					appliedFilters.length > 0 || selectedSidebarFilters.length > 0
						? {}
						: { opacity: 0.5 }
				}
			>
				{isInFiltersPolishExperimentVariant
					? getStagedFilteredResultsCtaText(stagedResultsCount)
					: 'Save'}
			</PrimaryCTA>
		);
	};

	const secondary = () => {
		afterNextPaint(() => {
			if (isInFiltersPolishExperimentVariant) {
				setSelectedSidebarFilters([]);
				// price shares its atoms with a pill rendered outside the sidebar;
				// only clear it here if this category actually manages it in-sidebar
				if (isPricingInSidebar) {
					clear();
				}
				return;
			}

			setIsSidebarOpen(false);
		});
	};

	const renderSecondaryCTA = () => {
		if (isMobile) {
			return null;
		}

		return (
			<>
				{isInFiltersPolishExperimentVariant ? (
					<SecondaryCTA onClick={secondary} color={'tertiary'}>
						Clear all
					</SecondaryCTA>
				) : (
					<SecondaryCTA onClick={secondary} color={'secondary'}>
						Cancel
					</SecondaryCTA>
				)}
			</>
		);
	};

	return (
		<SidebarUnion isOpen={isSidebarOpen} onClose={onClose}>
			<div className={Styles.sidebarWrapper} ref={sidebarContentRef}>
				<Pane
					header={
						isMobile
							? ((<MobileHeader />) as unknown as string)
							: 'More filters'
					}
					renderPrimaryCTA={renderPrimaryCTA}
					renderSecondaryCTA={renderSecondaryCTA}
				>
					<SidebarOptions isMobile={isMobile} />
				</Pane>
			</div>
		</SidebarUnion>
	);
};

export default React.memo(Sidebar);
